/**
 * SnapRaycastProvider — screen-space nearest-neighbor snap targeting.
 *
 * Instead of raycasting (which only finds what's directly under the mouse),
 * this projects all candidate targets to screen space and picks the one
 * closest to the mouse cursor. This gives precise, predictable snapping
 * regardless of viewing angle or surface topology — the same approach
 * Blender uses.
 *
 * Mounted inside the R3F Canvas (Viewport3D).
 */

import { useThree } from "@react-three/fiber";
import { useCallback, useEffect } from "react";
import * as THREE from "three/webgpu";
import { getSceneObjectWorldTransform } from "../app/collectionGraph.ts";
import { composeGroupTransform } from "../app/groupTransform.ts";
import { useStore } from "../app/store.ts";
import type { SnapTarget } from "../app/storeTypes.ts";
import { registerCursorSurfaceRaycast } from "../modeling/cursorSurfaceRaycast.ts";
import { computeFaceNormal } from "../modeling/faceNormal.ts";
import { validateSceneObject } from "../modeling/sceneObjectValidation.ts";
import {
  getDynamicSnapSpacing,
  registerSnapRaycast,
  type SnapResult,
} from "../modeling/snap.ts";
import {
  closestPointOnEdge,
  faceCentroid,
  topologyFaceCount,
} from "../modeling/topology.ts";
import {
  queryViewportSurface,
  type ViewportSurfaceHit,
} from "../modeling/viewportSurfaceRaycast.ts";
import { makeScratch } from "./scratch.ts";

// Scratch vectors — avoid per-call allocations
const tmp = makeScratch({
  proj: "vec3",
  objMatrixWorld: "mat4",
  quat: "quat",
  euler: "euler",
  pos: "vec3",
  scl: "vec3",
  normalMatrix: "mat3",
  normal: "vec3",
});

const tmpFaceNormal: [number, number, number] = [0, 0, 0];

/** Max screen-space distance (pixels) for a snap to engage. */
const SNAP_THRESHOLD_PX = 24;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Project a local-space point through an object's world matrix + camera. */
function projectToScreen(
  lx: number,
  ly: number,
  lz: number,
  objMatrix: THREE.Matrix4,
  camera: THREE.Camera,
  halfW: number,
  halfH: number,
): [number, number, number] {
  tmp.proj.set(lx, ly, lz).applyMatrix4(objMatrix).project(camera);
  return [(tmp.proj.x + 1) * halfW, (1 - tmp.proj.y) * halfH, tmp.proj.z];
}

/** Build a world matrix from position/rotation/scale arrays. */
function buildWorldMatrix(
  pos: readonly number[],
  rot: readonly number[],
  scl: readonly number[],
): THREE.Matrix4 {
  return tmp.objMatrixWorld.compose(
    tmp.pos.set(pos[0], pos[1], pos[2]),
    tmp.quat.setFromEuler(tmp.euler.set(rot[0], rot[1], rot[2])),
    tmp.scl.set(scl[0], scl[1], scl[2]),
  );
}

// ---------------------------------------------------------------------------
// Vertex snap — project all vertices, find screen-space nearest
// ---------------------------------------------------------------------------

function snapVertex(
  mousePx: number,
  mousePy: number,
  camera: THREE.Camera,
  halfW: number,
  halfH: number,
  excludeVertices: ReadonlySet<number> | null,
): SnapResult | null {
  const state = useStore.getState();
  const editMode = state.editMode;

  let bestDistSq = SNAP_THRESHOLD_PX * SNAP_THRESHOLD_PX;
  let bestWorld: [number, number, number] | null = null;
  let bestLocal: [number, number, number] | null = null;

  // --- Scene objects ---
  for (const obj of state.sceneObjects.values()) {
    if (validateSceneObject(obj)) continue;
    if (state.visibility.get(obj.id) === false) continue;
    const verts = obj.vertices;
    if (!verts || verts.length === 0) continue;
    const worldTransform = getSceneObjectWorldTransform(
      state.sceneObjects,
      state.collections,
      obj.id,
    ) ?? {
      position: obj.position,
      rotation: obj.rotation,
      scale: obj.scale,
    };
    const mat = buildWorldMatrix(
      worldTransform.position,
      worldTransform.rotation,
      worldTransform.scale,
    );
    const vertCount = verts.length / 3;
    const isEditObject = editMode?.objectId === obj.id;

    for (let i = 0; i < vertCount; i++) {
      // In edit mode, skip selected vertices (they're being moved)
      if (isEditObject && excludeVertices?.has(i)) continue;

      const lx = verts[i * 3],
        ly = verts[i * 3 + 1],
        lz = verts[i * 3 + 2];
      const [sx, sy, sz] = projectToScreen(
        lx,
        ly,
        lz,
        mat,
        camera,
        halfW,
        halfH,
      );

      // Skip vertices behind the camera
      if (sz < 0 || sz > 1) continue;

      const dx = sx - mousePx,
        dy = sy - mousePy;
      const dSq = dx * dx + dy * dy;
      if (dSq < bestDistSq) {
        bestDistSq = dSq;
        tmp.proj.set(lx, ly, lz).applyMatrix4(mat);
        bestWorld = [tmp.proj.x, tmp.proj.y, tmp.proj.z];
        bestLocal = [lx, ly, lz];
      }
    }
  }

  // --- Blend objects ---
  for (const entry of state.blendFiles.values()) {
    for (const obj of entry.file.objects) {
      if (!obj.meshRef) continue;
      if (state.visibility.get(obj.name) === false) continue;
      const mesh = entry.file.meshes.get(obj.meshRef);
      if (!mesh) continue;
      const verts = mesh.positions;
      const worldTransform = composeGroupTransform(entry.groupTransform, {
        position: obj.localPosition,
        rotation: obj.localRotation,
        scale: obj.localScale,
      });
      const mat = buildWorldMatrix(
        worldTransform.position,
        worldTransform.rotation,
        worldTransform.scale,
      );
      const vertCount = verts.length / 3;

      for (let i = 0; i < vertCount; i++) {
        const lx = verts[i * 3],
          ly = verts[i * 3 + 1],
          lz = verts[i * 3 + 2];
        const [sx, sy, sz] = projectToScreen(
          lx,
          ly,
          lz,
          mat,
          camera,
          halfW,
          halfH,
        );
        if (sz < 0 || sz > 1) continue;

        const dx = sx - mousePx,
          dy = sy - mousePy;
        const dSq = dx * dx + dy * dy;
        if (dSq < bestDistSq) {
          bestDistSq = dSq;
          tmp.proj.set(lx, ly, lz).applyMatrix4(mat);
          bestWorld = [tmp.proj.x, tmp.proj.y, tmp.proj.z];
          bestLocal = [lx, ly, lz];
        }
      }
    }
  }

  if (!bestWorld || !bestLocal) return null;
  return {
    worldPosition: bestWorld,
    localPosition: bestLocal,
    hitType: "vertex",
  };
}

// ---------------------------------------------------------------------------
// Edge snap — project edge endpoints, find screen-space nearest edge
// ---------------------------------------------------------------------------

function snapEdge(
  mousePx: number,
  mousePy: number,
  camera: THREE.Camera,
  halfW: number,
  halfH: number,
): SnapResult | null {
  const state = useStore.getState();

  let bestDistSq = SNAP_THRESHOLD_PX * SNAP_THRESHOLD_PX;
  let bestWorld: [number, number, number] | null = null;
  let bestLocal: [number, number, number] | null = null;

  for (const obj of state.sceneObjects.values()) {
    if (validateSceneObject(obj)) continue;
    if (state.visibility.get(obj.id) === false) continue;
    const verts = obj.vertices;
    const topo = obj.topology;
    if (!verts || !topo) continue;

    const worldTransform = getSceneObjectWorldTransform(
      state.sceneObjects,
      state.collections,
      obj.id,
    ) ?? {
      position: obj.position,
      rotation: obj.rotation,
      scale: obj.scale,
    };
    const mat = buildWorldMatrix(
      worldTransform.position,
      worldTransform.rotation,
      worldTransform.scale,
    );
    const faceCount = topologyFaceCount(topo);

    // Iterate polygon edges via topology
    const seen = new Set<number>();
    for (let f = 0; f < faceCount; f++) {
      const start = topo.offsets[f];
      const size = topo.offsets[f + 1] - start;
      for (let j = 0; j < size; j++) {
        const v0 = topo.corners[start + j];
        const v1 = topo.corners[start + ((j + 1) % size)];
        // Canonical edge key to avoid duplicates
        const eKey = v0 < v1 ? v0 * 1000000 + v1 : v1 * 1000000 + v0;
        if (seen.has(eKey)) continue;
        seen.add(eKey);

        const [sx0, sy0, sz0] = projectToScreen(
          verts[v0 * 3],
          verts[v0 * 3 + 1],
          verts[v0 * 3 + 2],
          mat,
          camera,
          halfW,
          halfH,
        );
        const [sx1, sy1, sz1] = projectToScreen(
          verts[v1 * 3],
          verts[v1 * 3 + 1],
          verts[v1 * 3 + 2],
          mat,
          camera,
          halfW,
          halfH,
        );

        // Skip edges behind camera
        if ((sz0 < 0 || sz0 > 1) && (sz1 < 0 || sz1 > 1)) continue;

        // Closest point on 2D screen-space edge segment to mouse
        const result2d = closestPointOnEdge(
          [sx0, sy0, 0],
          [sx1, sy1, 0],
          [mousePx, mousePy, 0],
        );
        const dx = result2d.point[0] - mousePx;
        const dy = result2d.point[1] - mousePy;
        const dSq = dx * dx + dy * dy;

        if (dSq < bestDistSq) {
          bestDistSq = dSq;
          // Compute the 3D local-space point at the same parametric t
          const t = result2d.t;
          const a: [number, number, number] = [
            verts[v0 * 3],
            verts[v0 * 3 + 1],
            verts[v0 * 3 + 2],
          ];
          const b: [number, number, number] = [
            verts[v1 * 3],
            verts[v1 * 3 + 1],
            verts[v1 * 3 + 2],
          ];
          bestLocal = [
            a[0] + (b[0] - a[0]) * t,
            a[1] + (b[1] - a[1]) * t,
            a[2] + (b[2] - a[2]) * t,
          ];
          tmp.proj.set(...bestLocal).applyMatrix4(mat);
          bestWorld = [tmp.proj.x, tmp.proj.y, tmp.proj.z];
        }
      }
    }
  }

  if (!bestWorld || !bestLocal) return null;
  return {
    worldPosition: bestWorld,
    localPosition: bestLocal,
    hitType: "edge",
  };
}

// ---------------------------------------------------------------------------
// Face snap — project face centroids, find screen-space nearest
// ---------------------------------------------------------------------------

function snapFace(
  mousePx: number,
  mousePy: number,
  camera: THREE.Camera,
  halfW: number,
  halfH: number,
): SnapResult | null {
  const state = useStore.getState();

  let bestDistSq = SNAP_THRESHOLD_PX * SNAP_THRESHOLD_PX;
  let bestWorld: [number, number, number] | null = null;
  let bestLocal: [number, number, number] | null = null;
  let bestWorldNormal: [number, number, number] | undefined;

  for (const obj of state.sceneObjects.values()) {
    if (validateSceneObject(obj)) continue;
    if (state.visibility.get(obj.id) === false) continue;
    const verts = obj.vertices;
    const topo = obj.topology;
    if (!verts || !topo) continue;

    const worldTransform = getSceneObjectWorldTransform(
      state.sceneObjects,
      state.collections,
      obj.id,
    ) ?? {
      position: obj.position,
      rotation: obj.rotation,
      scale: obj.scale,
    };
    const mat = buildWorldMatrix(
      worldTransform.position,
      worldTransform.rotation,
      worldTransform.scale,
    );
    tmp.normalMatrix.getNormalMatrix(mat);
    const fCount = topologyFaceCount(topo);

    for (let f = 0; f < fCount; f++) {
      const center = faceCentroid(verts, topo, f);
      const [sx, sy, sz] = projectToScreen(
        center[0],
        center[1],
        center[2],
        mat,
        camera,
        halfW,
        halfH,
      );
      if (sz < 0 || sz > 1) continue;

      const dx = sx - mousePx,
        dy = sy - mousePy;
      const dSq = dx * dx + dy * dy;
      if (dSq < bestDistSq) {
        bestDistSq = dSq;
        bestLocal = center;
        tmp.proj.set(...center).applyMatrix4(mat);
        bestWorld = [tmp.proj.x, tmp.proj.y, tmp.proj.z];
        const normal = computeFaceNormal(verts, topo, f, tmpFaceNormal);
        if (normal) {
          tmp.normal
            .set(normal[0], normal[1], normal[2])
            .applyMatrix3(tmp.normalMatrix)
            .normalize();
          bestWorldNormal = [tmp.normal.x, tmp.normal.y, tmp.normal.z];
        } else {
          bestWorldNormal = undefined;
        }
      }
    }
  }

  if (!bestWorld || !bestLocal) return null;
  return {
    worldPosition: bestWorld,
    localPosition: bestLocal,
    worldNormal: bestWorldNormal,
    hitType: "face",
  };
}

// ---------------------------------------------------------------------------
// Object snap — find closest object origin in screen space
// ---------------------------------------------------------------------------

function snapObject(
  mousePx: number,
  mousePy: number,
  camera: THREE.Camera,
  halfW: number,
  halfH: number,
  excludeIds: Set<string>,
): SnapResult | null {
  const state = useStore.getState();

  let bestDistSq = SNAP_THRESHOLD_PX * SNAP_THRESHOLD_PX;
  let bestPos: [number, number, number] | null = null;

  // Scene objects
  for (const obj of state.sceneObjects.values()) {
    if (excludeIds.has(obj.id)) continue;
    if (state.visibility.get(obj.id) === false) continue;
    const worldTransform = getSceneObjectWorldTransform(
      state.sceneObjects,
      state.collections,
      obj.id,
    ) ?? {
      position: obj.position,
      rotation: obj.rotation,
      scale: obj.scale,
    };
    const [sx, sy, sz] = projectToScreen(
      worldTransform.position[0],
      worldTransform.position[1],
      worldTransform.position[2],
      new THREE.Matrix4(),
      camera,
      halfW,
      halfH,
    );
    if (sz < 0 || sz > 1) continue;
    const dx = sx - mousePx,
      dy = sy - mousePy;
    const dSq = dx * dx + dy * dy;
    if (dSq < bestDistSq) {
      bestDistSq = dSq;
      bestPos = worldTransform.position;
    }
  }

  // Blend objects
  for (const entry of state.blendFiles.values()) {
    for (const obj of entry.file.objects) {
      if (excludeIds.has(obj.name)) continue;
      if (state.visibility.get(obj.name) === false) continue;
      const worldTransform = composeGroupTransform(entry.groupTransform, {
        position: obj.localPosition,
        rotation: obj.localRotation,
        scale: obj.localScale,
      });
      const pos = worldTransform.position;
      const [sx, sy, sz] = projectToScreen(
        pos[0],
        pos[1],
        pos[2],
        new THREE.Matrix4(),
        camera,
        halfW,
        halfH,
      );
      if (sz < 0 || sz > 1) continue;
      const dx = sx - mousePx,
        dy = sy - mousePy;
      const dSq = dx * dx + dy * dy;
      if (dSq < bestDistSq) {
        bestDistSq = dSq;
        bestPos = pos;
      }
    }
  }

  if (!bestPos) return null;
  return { worldPosition: bestPos, localPosition: bestPos, hitType: "object" };
}

// ---------------------------------------------------------------------------
// Grid snap — snap to world-space grid increments
// ---------------------------------------------------------------------------

function snapGrid(
  ndcX: number,
  ndcY: number,
  camera: THREE.Camera,
  spacing: number,
): SnapResult | null {
  // Cast a ray from the camera through the mouse position and intersect
  // with the axis-aligned plane most facing the camera. This avoids
  // degenerate intersections when the ray is nearly parallel to any
  // single fixed plane (e.g. Z=0 fails for Z-constrained snapping).
  const origin = new THREE.Vector3();
  const direction = new THREE.Vector3();

  // Compute ray from NDC
  origin.set(ndcX, ndcY, 0).unproject(camera);
  direction.set(ndcX, ndcY, 1).unproject(camera).sub(origin).normalize();

  // Pick the axis plane whose normal has the largest dot with the view
  // direction — that plane gives the most stable intersection.
  const ax = Math.abs(direction.x);
  const ay = Math.abs(direction.y);
  const az = Math.abs(direction.z);
  const planeNormal = new THREE.Vector3(
    ax >= ay && ax >= az ? 1 : 0,
    ay > ax && ay >= az ? 1 : 0,
    az > ax && az > ay ? 1 : 0,
  );

  const denom = planeNormal.dot(direction);
  let hitPoint: THREE.Vector3 | null = null;
  if (Math.abs(denom) > 1e-6) {
    const t = -planeNormal.dot(origin) / denom;
    if (t > 0) {
      hitPoint = origin.clone().addScaledVector(direction, t);
    }
  }

  if (!hitPoint) {
    // Fallback: project 10 units along the ray
    hitPoint = origin.clone().addScaledVector(direction, 10);
  }

  // Snap to grid
  const snapped: [number, number, number] = [
    Math.round(hitPoint.x / spacing) * spacing,
    Math.round(hitPoint.y / spacing) * spacing,
    Math.round(hitPoint.z / spacing) * spacing,
  ];

  return { worldPosition: snapped, localPosition: snapped, hitType: "grid" };
}

function queryVisibleSurfaceAtNdc(
  ndcX: number,
  ndcY: number,
  excludeIds: ReadonlySet<string>,
): ViewportSurfaceHit | null {
  return queryViewportSurface(ndcX, ndcY, excludeIds);
}

function snapSurface(
  ndcX: number,
  ndcY: number,
  excludeIds: ReadonlySet<string>,
): SnapResult | null {
  const hit = queryVisibleSurfaceAtNdc(ndcX, ndcY, excludeIds);
  if (!hit) return null;
  return {
    worldPosition: hit.worldPosition,
    localPosition: hit.worldPosition,
    worldNormal: hit.normal,
    hitType: "surface",
  };
}

// ---------------------------------------------------------------------------
// React component
// ---------------------------------------------------------------------------

export function SnapRaycastProvider() {
  const { camera, size } = useThree();

  const snapQuery = useCallback(
    (
      ndcX: number,
      ndcY: number,
      target: SnapTarget,
      excludeIds: Set<string>,
      snapGridLevelOffset = 0,
    ): SnapResult | null => {
      const halfW = size.width / 2;
      const halfH = size.height / 2;
      const mousePx = (ndcX + 1) * halfW;
      const mousePy = (1 - ndcY) * halfH;

      // In edit mode, get selected vertices to exclude from snap targets
      const state = useStore.getState();
      const excludeVertices = state.editMode?.selectedVertices ?? null;

      const spacing = getDynamicSnapSpacing(
        state.gridLogDist,
        snapGridLevelOffset,
      );

      switch (target) {
        case "grid":
          return snapGrid(ndcX, ndcY, camera, spacing);
        case "vertex":
          return snapVertex(
            mousePx,
            mousePy,
            camera,
            halfW,
            halfH,
            excludeVertices,
          );
        case "edge":
          return snapEdge(mousePx, mousePy, camera, halfW, halfH);
        case "face":
          return snapFace(mousePx, mousePy, camera, halfW, halfH);
        case "surface":
          return snapSurface(ndcX, ndcY, excludeIds);
        case "object":
          return snapObject(mousePx, mousePy, camera, halfW, halfH, excludeIds);
      }
    },
    [camera, size],
  );

  useEffect(() => {
    registerSnapRaycast(snapQuery);
    return () => registerSnapRaycast(null);
  }, [snapQuery]);

  const surfaceQuery = useCallback(
    (ndcX: number, ndcY: number): [number, number, number] | null => {
      return queryViewportSurface(ndcX, ndcY)?.worldPosition ?? null;
    },
    [],
  );

  useEffect(() => {
    registerCursorSurfaceRaycast(surfaceQuery);
    return () => registerCursorSurfaceRaycast(null);
  }, [surfaceQuery]);

  return null;
}
